/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/managerApi.ts
 * PURPOSE: Manager Dashboard + Approval Queue API client — Sprint 75 S75-001/S75-002
 *          + ISS-MGR-8 (2026-08-09) date-range attendance filter types
 *          + 2026-08-17: ISS-MGR-11 (§27) — new getHoursSummary() +
 *          EmployeeHoursItem/TeamHoursTotals/HoursSummaryData types (mirror
 *          manager_hub.py HoursSummaryResponse, DD-0093) for Manager Hub widget [4].
 *          + 2026-08-17: DD-0103 (ISS-PILOT-17 "thống kê" half) — added
 *          days_over_ot_cap/insufficient_rest_days (EmployeeHoursItem) +
 *          employees_over_ot_cap/employees_insufficient_rest (TeamHoursTotals).
 *          + 2026-09-14: copyRangePreview/copyRangeCommit + CopyRange* types
 *          (generalize copyScheduleFromWeek — WIREFRAME §SC-009 [17]/[17b]) —
 *          modal "Sao chép lịch" khoảng ngày tuỳ ý (nguồn/đích N≠M, lặp chu kỳ).
 *          + 2026-09-14 (tiếp): getScheduleAuditHistory + ScheduleAudit* types
 *          — panel [6] "Nhật ký thay đổi lịch" (route/content_kind/source_date/
 *          is_self_proposed), mirror backend AuditHistoryResponse mở rộng.
 *          + 2026-09-15: ScheduleCellResult.locked_count (khớp backend
 *          CellUpdateResponse.locked_count có sẵn từ 2026-09-07 GAP #1 —
 *          FE trước đó chưa khai trường này, [R42] "Nghỉ (bỏ hết ca)" mất
 *          phần phản hồi "Còn N khối đã bắt đầu, không xoá được.").
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-15
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P2 (Sprint Governance) + P4 (Quality Gates)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs (<50ms p95)
 * SPRINT: S75-001 (managerApi.ts) + S75-002 (connect to backend endpoints)
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — Manager Decision Support
 * - Impact: Revenue Critical — real-time KPIs, approval queue, bulk actions
 * - Stakeholders: Branch Managers, Operations Team
 *
 * @technical-architecture
 * - Layer: Data Access Layer (API Services)
 * - Pattern: Axios HTTP client, TypeScript strict types, Zero Mock
 * - Dependencies: axios (via api from config), TypeScript 5.0+
 *
 * @cross-references
 * - Backend: backend/app/api/v2/endpoints/manager_approvals.py
 * - Hooks: frontend/src/hooks/useManagerDashboard.ts
 * - Design: docs/02-design/04-ui-ux-design/SPEC-0407-manager-dashboards-design.md
 * - DD-0016: docs/02-design/18-adrs/06-decisions/DD-0016-manager-branch-access-control.md
 * ===============================================================================
 */

import { api, apiV2 } from "./config";

// =============================================================================
// Response Types — mirror backend Pydantic models
// =============================================================================

export interface ManagerKPIData {
  present_today: number;
  total_staff: number;
  late_today: number;
  pending_approvals: number;
  on_time_rate: number;
  absent_today: number;
  overtime_today: number;
  scheduled_today?: number;  // ISS-018-A: "Đang ca" card — scheduled count (fallback: total_staff)
  // ISS-018-E: Trend indicators — delta vs yesterday (SPEC-0407 FR-002)
  delta_present?: number;    // today.present - yesterday.present
  delta_late?: number;       // today.late - yesterday.late
  delta_absent?: number;     // today.absent - yesterday.absent
  as_of: string;
  branch_id?: string | null;  // S95L-008: manager's branch UUID for zone filter
}

export interface ManagerAlertItem {
  alert_type: "late_arrival" | "absent" | "pending_approval" | "overtime";
  severity: "high" | "medium" | "low";
  message: string;
  employee_name?: string;
  employee_id?: string;
  timestamp: string;
  metadata: Record<string, unknown>;
}

export interface ManagerAlertsData {
  alerts: ManagerAlertItem[];
  total_count: number;
  high_priority_count: number;
}

export interface ActiveStaffMember {
  user_id: string;
  full_name: string;
  clock_in_time: string;
  hours_worked: number;
  status: string;
  is_overtime: boolean;
  position?: string;        // ISS-018-N: Vai trò/chức vụ (SC-02 S2.7)
  work_area?: string;       // ISS-018-C: Kitchen, Service, Bar, etc.
  late_minutes?: number;    // ISS-018-H: minutes late (>0 when status=late)
}

export interface ActiveStaffData {
  active_staff: ActiveStaffMember[];
  total_active: number;
  as_of: string;
}

export interface PendingApprovalItem {
  id: string;
  approval_type: "shift_swap" | "leave_request";
  requester_name: string;
  requester_id: string;
  description: string;
  requested_at: string;
  priority: "high" | "medium" | "low";
  metadata: Record<string, unknown>;
}

export interface PendingApprovalsData {
  items: PendingApprovalItem[];
  total_count: number;
  shift_swap_count: number;
  leave_request_count: number;
  as_of: string;
}

export interface BulkApproveRequest {
  item_ids: string[];
  approval_type: "shift_swap" | "leave_request";
  action: "approve" | "reject";
  reason?: string;
}

export interface BulkApproveResult {
  item_id: string;
  success: boolean;
  message: string;
}

export interface BulkApproveResponse {
  processed: number;
  succeeded: number;
  failed: number;
  results: BulkApproveResult[];
  action: string;
  approval_type: string;
  processed_by: string;
  processed_at: string;
}

// =============================================================================
// Release v0 WP-2 / SC-010-HUB-BM — Manager Hub types (mirror manager_hub.py)
// =============================================================================

export interface MissingStaffItem {
  user_id: string;
  full_name: string;
  shift_label: string; // "HH:MM–HH:MM"
}

export interface CoverageAreaItem {
  work_area_id: string | null;
  work_area_name: string;
  scheduled_shifts: number;
  clocked_in: number;
  pending_approval: number;
  missing_clock_in: number;
  late_count: number;
  gps_deviation_count: number;
  missing_staff: MissingStaffItem[];
}

export interface CoverageTodayData {
  date: string;
  branch_name: string | null;
  branch_code: string | null;
  areas: CoverageAreaItem[];
  totals: {
    staff: number;
    shifts: number;
    clocked_in: number;
    pending_approval: number;
    missing_clock_in: number;
  };
  as_of: string;
}

export interface WeekSummaryData {
  week_start: string;
  week_end: string;
  week_number: number;
  status: "none" | "draft" | "published";
  shift_count: number;
  staff_count: number;
  warnings_count: number;
  over_cap_count: number;
  rest_violation_count: number;
  /**
   * BOT-048 (#833): SSOT publish_ahead_days per-tenant — dùng để render dòng
   * nhắc xuất bản [6] "Nên xuất bản trước tối thiểu N ngày" ở ManagerHubPage.
   * Backend: manager_hub.py::get_week_summary() tái dùng
   * manager_schedule.py::_get_schedule_rules() (cùng nguồn dialog [16]).
   */
  publish_ahead_days: number;
  branch_name: string | null;
  branch_code: string | null;
  as_of: string;
}

export interface RemindMissingRequest {
  work_area_id?: string | null;
}

export interface RemindMissingResponse {
  reminded_count: number;
  work_area_id: string | null;
  work_area_name: string | null;
  staff: MissingStaffItem[];
  audited: boolean;
  as_of: string;
}

/**
 * ISS-MGR-11 / DD-0093: widget MỚI "Tổng giờ làm" (tuần/tháng, per nhân viên).
 * days_over_ot_cap/insufficient_rest_days: DD-0103 (ISS-PILOT-17 "thống kê" half) —
 * tính từ Schedule (kế hoạch), cùng công thức cổng publish (Điều 107/111 BLLĐ 2019).
 */
export interface EmployeeHoursItem {
  user_id: string;
  full_name: string;
  work_area_id: string | null;
  work_area_name: string;
  scheduled_shifts: number;
  worked_shifts: number;
  total_hours: number;
  /** Tổng giờ OT ĐÃ ĐĂNG KÝ (BS-011-R11) — không tính giờ ra muộn tự động */
  overtime_hours: number;
  average_hours_per_shift: number;
  /** Số ngày CA ĐÃ XẾP vượt trần OT/ngày đã cấu hình (Điều 107) */
  days_over_ot_cap: number;
  /** Chỉ có giá trị khi period=week; null khi period=month */
  insufficient_rest_days: boolean | null;
}

export interface TeamHoursTotals {
  staff: number;
  total_hours: number;
  overtime_hours: number;
  average_hours_per_employee: number;
  /** Số nhân viên có >=1 ngày vượt trần OT/ngày */
  employees_over_ot_cap: number;
  /** Số nhân viên thiếu ngày nghỉ/tuần (period=week only, 0 khi period=month) */
  employees_insufficient_rest: number;
}

export interface HoursSummaryData {
  period: "week" | "month";
  range_start: string;
  range_end: string;
  branch_name: string | null;
  branch_code: string | null;
  employees: EmployeeHoursItem[];
  totals: TeamHoursTotals;
  as_of: string;
}

export interface HoursSummaryParams {
  period: "week" | "month";
  anchor_date?: string;
}

// =============================================================================
// Release v0 WP-3 / SC-011-ATTEND-MGR — Chấm công đội types (mirror manager_attendance.py)
// =============================================================================

/** Trạng thái dòng-ca — FE map sang nhãn VI verbatim mockup (● ▲ ▼ ⚠ ○) */
export type AttendanceRowStatus =
  | "on_time"
  | "late"
  | "early_leave"
  | "missing"
  | "gps_deviation"
  | "upcoming"
  /** Bổ sung 2026-08-17: NV chấm công dù không có ca hiệu lực hôm đó (ca đã
   * bị huỷ, hoặc chưa từng có ca) — luôn "chờ duyệt" tay, không tự động. */
  | "unscheduled";

/**
 * 1 dòng khối [18e] "Xử lý ngoại lệ giờ công" (modal [18] SC-011).
 * - `overtime` "Ngoài giờ": khung `from_time`–`to_time` (HH:MM) → cộng vào Giờ OT.
 * - `adjustment` "Điều chỉnh": `hours` ± → cộng/trừ thẳng Giờ công, `note` = Lý do bắt buộc.
 */
export interface AttendanceExceptionLine {
  id?: string | null;
  kind: "overtime" | "adjustment";
  from_time?: string | null;
  to_time?: string | null;
  hours?: number | null;
  note?: string | null;
}

export interface TeamAttendanceRow {
  attendance_id: string | null; // null khi chưa có bản ghi (Thiếu chấm / Chưa tới ca)
  /** SC-011 (2026-08-17): id ổn định cho /resolve — attendance_id nếu đã có bản ghi,
   *  ngược lại "schedule:{schedule_id}" cho dòng "Thiếu chấm công" chưa có Attendance. */
  attendance_ref: string | null;
  schedule_id: string;
  user_id: string;
  full_name: string;
  date: string;                 // YYYY-MM-DD — ngày ca theo lịch (ISS-MGR-8, DD-0095: neo theo ngày bắt đầu ca)
  /** BS-011 R27-R30 (2026-08-18, F2 vá): neo `Schedule.organization_id` (KHÔNG
   *  còn `Schedule.work_area_id` — FK cũ deprecated, luôn null trước đây).
   *  `work_area_name` ĐỘNG: cấp work_area thật → tên khu vực; cấp khác (chi
   *  nhánh/vùng/...) → "Nhân viên {tên node}"; không resolve được → "Nhân viên
   *  {tenant.display_name}". Tên field GIỮ NGUYÊN cho tương thích contract. */
  work_area_id: string | null;
  work_area_name: string;
  /** Mã ca từ ShiftTemplate khớp giờ (vd M6, A2, N7); null → chỉ hiển thị giờ */
  shift_code: string | null;
  shift_label: string;          // "HH:MM–HH:MM"
  shift_type: string | null;    // morning|afternoon|evening|night|...
  scheduled_start: string;      // "HH:MM"
  scheduled_end: string;        // "HH:MM"
  clock_in: string | null;      // ISO datetime
  clock_out: string | null;     // ISO datetime
  /** SC-011 "Được tính" — §3.2/§3.4 BS-400, null khi unmatched/missing. */
  computed_check_in: string | null;
  computed_check_out: string | null;
  late_minutes: number | null;
  early_leave_minutes: number | null;
  work_hours: number | null;      // VĐ2: "Giờ thực tế" — giờ chấm công thật
  /** VĐ2 (2026-08-16): "Giờ phân công" — theo lịch, KHÔNG đổi theo chấm công thực */
  assigned_hours: number | null;
  /** Bổ sung 2026-08-17: số phút nghỉ giữa ca theo cấu hình mã ca — hiện rõ trong "Xem chi tiết" */
  break_minutes: number | null;
  /** VĐ2 (2026-08-16): "Địa điểm" — chi nhánh của ca này (quan trọng khi có điều động liên chi nhánh, VĐ1) */
  branch_name: string | null;
  ot_type: "Sớm" | "Trễ" | null;
  ot_hours: number | null;
  ot_note: string | null;
  gps_ok: boolean | null;
  gps_distance_m: number | null;
  /** 2026-09-07 (sync wireframe/prototype modal [18] khối Ảnh/GPS) — toạ độ thô
   * lúc chấm công, vd hiển thị "10.93°N, 108.10°E". null khi chưa chấm/không có evidence. */
  gps_lat: number | null;
  gps_lng: number | null;
  /** "Địa điểm" [8] — tên schedule.organization_id, KHÔNG phải GPS; null khi không có schedule khớp. */
  location_name: string | null;
  status: AttendanceRowStatus;
  /** SC-011 đa nhãn "Trạng thái" — enum thô (vd "on_time","late") + bản VI render thẳng. */
  status_labels: string[];
  status_labels_vi: string[];
  /** Cột "Thao tác" [17] — thay _row_status() cũ. */
  action_state: "pending_review" | "recorded" | "not_recorded" | "upcoming";
  resolution_source: "auto" | "manager" | null;
  is_matched: boolean;
  /** "Có khiếu nại" — Phase backend hiện tại LUÔN false (Phase C nhân viên khiếu nại chưa build). */
  has_complaint: boolean;
  /**
   * [18-LOG] Mốc "Nhân viên khiếu nại". null khi `has_complaint=false` HOẶC
   * record cũ có complaint từ TRƯỚC khi cột này tồn tại (Zero-Mock: không suy
   * ngược mốc thời gian không có thật) — component PHẢI ẩn hẳn dòng này khi
   * null, không hiện mốc bịa dù `has_complaint=true`.
   */
  complaint_raised_at: string | null;
  /**
   * [18-LOG] Mốc "Công ghi nhận tự động" — set 1 lần đầu record đạt
   * `action_state=recorded` qua `resolution_source=auto`. null khi record
   * chưa từng tự-động-ghi-nhận (còn pending_review, hoặc recorded/
   * not_recorded qua tay Quản lý) — component ẩn dòng này khi null.
   */
  auto_recorded_at: string | null;
  /**
   * [18-LOG] "Lịch sử xử lý" — bổ sung 2026-08-26. Dữ liệu vốn đã có trong DB
   * (`attendances.manager_resolved_*`/`approved_*`/`rejected_*`) nhưng endpoint
   * chưa trả ra nên popup từng bị bỏ hẳn (Zero-Mock, không bịa dữ liệu). Nay đã
   * trả. `*_by_name` là tên người xử lý đã resolve sẵn ở backend.
   */
  manager_resolved_at: string | null;
  manager_resolved_by_name: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  rejected_at: string | null;
  rejected_by_name: string | null;
  is_approved: boolean;
  /** Bổ sung 2026-08-17 (nút "Từ chối" mới) — tách biệt "chờ duyệt" và "đã từ chối" */
  is_rejected: boolean;
  rejection_reason: string | null;
  flagged: boolean;
  flag_note: string | null;
  manager_notes: string | null;
  /** Ghi chú nhân viên tự nhập lúc chấm công — ĐỌC-ONLY trong modal Quản lý. */
  employee_note: string | null;
  /** #780: NV xin đóng ca tồn đọng không có GPS — chờ Quản lý "Ghi nhận công" (duyệt) hoặc "Từ chối đóng ca". */
  close_requested?: boolean;
  /** BOT-061 (#873, 2026-09-14) — lý do nhân viên nhập khi chấm công bằng
   *  Timemark (phương thức dự phòng cuối khi GPS/ảnh không khả dụng, xem
   *  `TimemarkForm.tsx`). Nguồn: `attendances.timemark_reason`. null khi
   *  record không chấm qua Timemark. ĐỌC-ONLY trong modal Quản lý, cùng
   *  quy tắc ẩn-khi-rỗng như `employee_note`. */
  timemark_reason: string | null;
  /** Khối [18e] "Xử lý ngoại lệ giờ công" — rỗng thì ẩn hẳn bảng. */
  exceptions: AttendanceExceptionLine[];
  has_clock_in_photo: boolean;
  /** VĐ4 (2026-08-16): cờ song song has_clock_in_photo cho lượt chấm ra */
  has_clock_out_photo: boolean;
}

/** SC-011 [10]⚙ — DD-0105 Tầng 2 "tự cấu hình theo cơ sở" (bổ sung 2026-08-26). */
export interface BranchWorkforceConfigValues {
  check_in_tolerance_minutes: number;
  check_out_early_tolerance_minutes: number;
  missed_check_in_threshold_minutes: number;
  auto_record_missed_check_out: boolean;
  auto_record_off_schedule: boolean;
  auto_record_missing: boolean;
  auto_record_unmatched: boolean;
}

export interface BranchWorkforceConfig {
  organization_id: string | null;
  branch_name: string | null;
  /** Tenant Admin cấp quyền theo NHÓM (2 công tắc độc lập, SC-004 khối [9]). */
  editable: { tolerance: boolean; auto_record: boolean };
  /** Giá trị đang có hiệu lực sau khi áp Tầng 2 lên Tầng 1. */
  effective: BranchWorkforceConfigValues;
  /** Giá trị mặc định của tổ chức — để đối chiếu "đang khác mặc định". */
  tenant_baseline: BranchWorkforceConfigValues;
  /** Giá trị cơ sở tự đặt (rỗng nếu chưa đặt). */
  override: Partial<BranchWorkforceConfigValues>;
}

export interface TeamAttendanceGroup {
  /** Cùng anchor/ngữ nghĩa với TeamAttendanceRow.work_area_id — xem đó. */
  work_area_id: string | null;
  work_area_name: string;
  rows: TeamAttendanceRow[];
}

export interface TeamAttendanceData {
  date: string;                 // YYYY-MM-DD — = date_from, giữ tương thích ngược
  date_from: string;            // YYYY-MM-DD — đầu khoảng ngày đã lọc (ISS-MGR-8)
  date_to: string;              // YYYY-MM-DD — cuối khoảng ngày đã lọc (ISS-MGR-8)
  is_future: boolean;
  late_threshold_minutes: number;
  gps_radius_meters: number;
  /** SC-011 CFG-20..23 (2026-08-17) — dung sai tenant-wide, echo lại cho FE hiển thị nếu cần. */
  check_in_tolerance_minutes: number;
  check_out_early_tolerance_minutes: number;
  missed_check_in_threshold_minutes: number;
  missed_check_out_threshold_minutes: number;
  /** Chip [6] "N cần xử lý" ở chế độ PHẲNG — số BẢN GHI, KHÔNG giới hạn ngày (2026-09-07, gỡ
   * cửa sổ 92 ngày cũ). Cũng dùng cho logic "tự nhảy khoảng xem đủ" (so khớp DÒNG hiển thị). */
  pending_count: number;
  /** Chip [6] ở chế độ GỘP (mặc định) — số NHÓM (nhân viên+ngày) có ít nhất 1 bản ghi pending,
   * đúng câu chữ wireframe registry khối [FILTER [6]]. LUÔN <= pending_count. */
  pending_count_grouped: number;
  /** Ngày tenant được tạo (`Tenant.created_at`) — mốc tham khảo hiển thị chung. KHÔNG dùng làm
   * cận dưới khi chip [6] nhảy [3] (xem `oldest_pending_date`) — "tenant không có dữ liệu trước
   * ngày này" không được ràng buộc ở tầng dữ liệu và đã đo được vi phạm thật (2026-09-12). */
  tenant_created_at: string;
  /** Ngày SỚM NHẤT trong số các bản ghi đang pending — tính trực tiếp từ chính tập dữ liệu
   * `pending_count` quét qua, không suy từ mốc nào khác. Cận dưới ĐÚNG DUY NHẤT để chip [6] tự
   * nhảy [3] và chắc chắn xem hết mọi pending. null khi `pending_count=0`. */
  oldest_pending_date: string | null;
  branch_name: string | null;
  branch_code: string | null;
  groups: TeamAttendanceGroup[];
  total_rows: number;
  as_of: string;
}

export interface TeamAttendanceParams {
  date?: string;                 // YYYY-MM-DD — chế độ 1-ngày-đơn (tương thích ngược)
  date_from?: string;            // YYYY-MM-DD — ISS-MGR-8 date-range (đi cùng date_to)
  date_to?: string;              // YYYY-MM-DD — ISS-MGR-8 date-range (đi cùng date_from)
  /** Tên param giữ nguyên cho tương thích ngược; F2 (2026-08-18) nay backend
   *  lọc theo Schedule.organization_id. ManagerAttendancePage hiện KHÔNG gửi
   *  param này (lọc client-side qua flatRows/UNASSIGNED_WORK_AREA). */
  work_area_id?: string;
  status?: string;              // on_time|late|early_leave|missing|gps_deviation|upcoming|pending|flagged
  /** SC-011 [1b] Tìm nhân viên (multi-select) — repeat param. */
  employee_ids?: string[];
}

export interface ResolveRequest {
  /** #780: "reject_close" = từ chối yêu cầu NV tự đóng ca tồn đọng (bản ghi mở lại). */
  action: "record" | "defer" | "not_record" | "reject_close";
  computed_check_in?: string | null;
  computed_check_out?: string | null;
  manager_note?: string | null;
  /** Toàn bộ danh sách [18e] sau khi sửa — server thay nguyên cụm. */
  exceptions?: AttendanceExceptionLine[];
}

export interface ResolveResult {
  attendance_id: string;
  action_state: "pending_review" | "recorded" | "not_recorded" | "upcoming";
  status_labels: string[];
  status_labels_vi: string[];
  computed_check_in: string | null;
  computed_check_out: string | null;
  work_hours: number | null;
  message: string;
  as_of: string;
}

/** 1 dòng trong thanh bulk "N đã chọn" (checkbox cột "Thao tác"). KHÔNG có
 * field điền-giùm riêng — gửi lại NGUYÊN giá trị hệ thống đã tự tính đang
 * hiển thị sẵn cho dòng đó (row.computed_check_in/out/exceptions), không
 * phải giá trị Quản lý tự gõ (khác hẳn `ResolveRequest` của modal đơn). */
export interface BulkResolveItem {
  ref: string; // attendance_id thật, hoặc "schedule:<id>" cho "Thiếu chấm công"
  computed_check_in?: string | null;
  computed_check_out?: string | null;
  exceptions?: AttendanceExceptionLine[];
}

export interface BulkResolveRequest {
  action: "record" | "defer";
  items: BulkResolveItem[];
  /** ECO-DEC-2026-144: lý do chung cho cả lô — bắt buộc khi action="record". */
  manager_note?: string;
}

export interface BulkResolveResultItem {
  ref: string;
  ok: boolean;
  message?: string | null;
  error?: string | null;
}

export interface BulkResolveResult {
  processed: number;
  failed: number;
  results: BulkResolveResultItem[];
  as_of: string;
}

export interface OTRequestPayload {
  ot_type: "Sớm" | "Trễ";
  hours: number;                // > 0
  note?: string;
}

export interface AttendanceActionResult {
  attendance_id: string;
  is_approved: boolean;
  /** Bổ sung 2026-08-17 (nút "Từ chối" mới) */
  is_rejected: boolean;
  rejection_reason: string | null;
  flagged: boolean;
  flag_note: string | null;
  ot_type: "Sớm" | "Trễ" | null;
  ot_hours: number | null;
  ot_note: string | null;
  audited: boolean;
  message: string;
  as_of: string;
}

// =============================================================================
// Release v0 WP-4 / SC-012-MGR-STAFF — Nhân viên types (mirror manager_staff.py)
// =============================================================================

/** Trạng thái hôm nay theo NGƯỜI — FE map nhãn VI verbatim mockup */
export type StaffTodayStatus =
  | "on_time"
  | "late"
  | "early_leave"
  | "missing"
  | "gps_deviation"
  | "upcoming"
  | "no_shift";

export interface StaffListItem {
  user_id: string;
  employee_code: string | null;
  full_name: string;
  role: string;             // TenantUserRole value (key kỹ thuật)
  role_label: string;       // Nhãn VI theo cấp đơn vị (Trưởng ca, Bếp trưởng, Nhân viên...)
  phone: string | null;
  work_area_id: string | null;
  work_area_name: string;
  has_shift_today: boolean;
  today_status: StaffTodayStatus;
  // VĐ1 (2026-08-16): điều động liên chi nhánh — additive, mặc định false/null
  // cho NV cơ hữu, không đổi hành vi cũ.
  is_cross_branch_assignment?: boolean;
  home_branch_name?: string | null;
  // ISS-MGR-22 (2026-08-19) — ADDITIVE, KHÁC work_area_id/work_area_name ở
  // trên (2 field đó null cố định "Chưa phân khu vực" khi NV không ở ĐÚNG cấp
  // work_area — R30 guardrail SC-012, KHÔNG đụng). 2 field MỚI này LUÔN trả
  // node organization THẬT của NV bất kể cấp (work_area/branch/region/...) —
  // dùng cho bộ lọc "Khu vực làm việc" màn Chấm công (SC-011), xem
  // `defaultAreaOptions` trong ManagerAttendancePage.tsx.
  raw_organization_id: string | null;   // null CHỈ khi user.organization_id là None
  raw_organization_name: string | null; // "Nhân viên {tên node}" / tên khu vực thật / "Nhân viên {tên tenant}"
}

export interface StaffListData {
  date: string;             // YYYY-MM-DD
  branch_name: string | null;
  branch_code: string | null;
  items: StaffListItem[];   // thứ tự roster (khu vực → tên), KHÔNG sort lại
  total_staff: number;      // N nhân viên (đếm [3] mockup)
  scheduled_today: number;  // M có ca hôm nay (đếm [3] mockup)
  as_of: string;
}

export interface StaffWeekShiftItem {
  schedule_id: string;
  shift_code: string | null; // Mã ca (M6, A2, N7...) — null → hiển thị shift_label
  shift_label: string;       // "HH:MM–HH:MM"
  scheduled_start: string;
  scheduled_end: string;
}

export interface StaffWeekDay {
  date: string;              // YYYY-MM-DD
  shifts: StaffWeekShiftItem[];
}

export interface StaffTodayShift {
  schedule_id: string;
  shift_code: string | null;
  shift_label: string;
  clock_in: string | null;   // ISO datetime
  clock_out: string | null;  // ISO datetime
  status: AttendanceRowStatus;
  is_approved: boolean;
}

export interface StaffWeekDetailData {
  user_id: string;
  full_name: string;
  role_label: string;
  phone: string | null;
  work_area_id: string | null;
  work_area_name: string;
  branch_name: string | null;
  branch_code: string | null;
  week_start: string;
  week_end: string;
  week_number: number;       // Tuần ISO (vd Tuần 31)
  days: StaffWeekDay[];      // đủ 7 ngày T2..CN; ngày nghỉ → shifts=[]
  today: string;
  today_shifts: StaffTodayShift[];
  as_of: string;
  /** Tổng kết "Đúng giờ" THÁNG hiện tại (S-MGR-ONTIME 2026-08-09) */
  month_shift_count: number | null;
  month_late_count: number | null;
  month_early_count: number | null;
  /** 0-100, null nếu NV chưa có hồ sơ Employee hoặc chưa có ca nào trong tháng */
  on_time_percent_month: number | null;
}

// =============================================================================
// Release v0 WP-6 / SC-009-SCHEDULE-GRID — Bảng phân ca tuần types
// (mirror manager_schedule.py — 8 endpoints /api/v2/manager/schedule/*)
// =============================================================================

export interface ScheduleRulesInfo {
  max_hours_week: number;      // Trần giờ/tuần (Điều 105)
  min_rest_hours: number;      // Nghỉ tối thiểu giữa 2 ca (Điều 110)
  publish_ahead_days: number;  // Công bố trước tối thiểu N ngày (dialog [16])
  violation_mode: string;      // "block" (Chặn) | "warn" (Chỉ cảnh báo)
  // 2026-09-14 (Rủi ro 1, gộp lưới N-ngày): backend (ISS-PILOT-16, xem
  // ScheduleRulesInfo trong manager_schedule.py) LUÔN trả field này —
  // FE trước đây chưa khai báo (drift kiểu type thiếu field thật, không phải
  // field mới). 1=Thứ 2…7=CN — ManagerSchedulePage.tsx dùng để tính đúng ranh
  // giới tuần-máy-chủ khi tenant cấu hình week_start_day khác Thứ 2 (trước đó
  // hardcode Thứ 2 → có thể BỎ SÓT ngày khi gộp nhiều tuần, xem `touchedWeekStarts`).
  // Backend còn trả thêm schedule_cycle/min_rest_days_week/max_overtime_day —
  // CHƯA khai báo ở đây vì FE chưa cần dùng (giữ interface tối thiểu những gì
  // đang tiêu thụ, tránh field khai rồi không ai đọc/không ai giữ đồng bộ).
  week_start_day: number;
}

export type ScheduleSource = "MANAGER" | "EMPLOYEE";
// [EXP] (2026-09-07, wireframe SC-009 §[EXP]): "EXPIRED" là giá trị TÍNH TOÁN
// LÚC ĐỌC ở backend (_is_proposal_expired) khi 1 đề xuất PENDING đã qua giờ
// bắt đầu ca — cột DB approval_status vẫn giữ "PENDING", FE chỉ thấy EXPIRED
// qua field này trong GridShiftItem.
export type ProposalApprovalStatus = "PENDING" | "APPROVED" | "REJECTED" | "EXPIRED";

// =============================================================================
// Đổi ca (Shift Swap) — Variant B, nối UI 2026-08-30 (xem DD-0106)
// Luồng 3 bước: NV xin đổi → NV được xin đổi đồng ý → Quản Lý duyệt/từ chối.
// =============================================================================

export interface ShiftSwapListItem {
  schedule_id: string;
  date: string;
  start_time: string;
  end_time: string;
  requester_employee_id: string;
  requester_name: string;
  target_employee_id: string;
  target_name: string;
  reason: string;
  warning_24h: boolean;
  swap_target_accepted: boolean | null;
  created_at: string;
}

export interface ShiftSwapApprovalPayload {
  swap_request_id: string;
  approved: boolean;
  manager_notes?: string;
}

export interface ShiftSwapApprovalResult {
  id: string;
  schedule_id: string;
  requester_employee_id: string;
  target_employee_id: string | null;
  status: string;
  mvv_code?: string | null;
}

export interface GridShiftItem {
  schedule_id: string;
  shift_code: string | null;   // Mã ca (M6/A2/N7…) — null → hiển thị shift_label
  shift_label: string;         // "HH:MM–HH:MM" (A4: "Ngày nghỉ" nếu is_day_off)
  start_time: string;
  end_time: string;
  shift_type: string | null;   // morning|afternoon|evening|night|…
  hours: number;
  /** A4 (2026-09-03): ca ngày nghỉ 0 giờ — chip xám "Ngày nghỉ", gán được mọi ngày (ECO-DEC-2026-144) */
  is_day_off?: boolean;
  status: string;              // draft | published | confirmed | …
  // Employee tự đăng ký ca (2026-08-09) — song song với Manager tự xếp ca.
  source: ScheduleSource;
  approval_status: ProposalApprovalStatus | null; // null khi source=MANAGER
  /** 2026-08-12: true nếu ca ở cơ sở KHÁC cơ sở nhà NV (điều động) */
  is_cross_branch: boolean;
  /** 2026-08-12: ghi chú Manager (VD lý do điều động) */
  manager_note: string | null;
  // Ý 3+4 (2026-08-16): NV bị điều động sang chi nhánh khác — true kèm tên/mã
  // chi nhánh ĐÍCH, FE hiện badge góc chip + khoá click (chỉ xem, không sửa).
  // DD-0104 §3.4: backend trả CẢ manager_note lẫn branch_name/branch_code.
  // 2026-08-18: tên/mã chi nhánh của CHÍNH ca này — hiện trên MỌI chip.
  branch_name: string | null;
  branch_code: string | null;
  /** [OT] — true khi khối "Phân theo giờ" (shift_code null) được tick "Tính
   * là OT" lúc tạo. FE hiện "· OT" sau khung giờ + nền turmeric (khác
   * gray-100 của khối giờ tuỳ chỉnh KHÔNG OT). Luôn false cho ca gán theo
   * mã ca. */
  is_overtime?: boolean;
}

export interface GridDayCell {
  date: string;                // YYYY-MM-DD
  day_label: string;           // T2..CN
  shifts: GridShiftItem[];
  warnings: string[];
}

export interface GridEmployeeRow {
  user_id: string;
  full_name: string;
  /** BR-SCHD-011 (Nhập nhanh [5]) — khớp `{mã NV}:` phía trình duyệt, không
   * gọi API riêng. Có thể null (NV chưa gán mã) — khi đó chỉ khớp theo tên. */
  employee_code: string | null;
  work_area_id: string | null;
  work_area_name: string;
  days: GridDayCell[];         // đủ 7 ô T2..CN
  total_hours: number;
  is_over_cap: boolean;
  warnings: string[];
  /** WIREFRAME-V0-MASTER-REGISTRY.md §SC-009 [11b]: NV "điều động ĐẾN" —
   * chi nhánh nhà khác chi nhánh đang xem. null khi NV thuộc roster gốc của
   * chi nhánh đang xem (không phải "điều động"). */
  transferred_from_branch_name: string | null;
}

export interface GridWorkAreaGroup {
  work_area_id: string | null;
  work_area_name: string;
  staff_count: number;
  employees: GridEmployeeRow[];
}

export type ScheduleWeekStatus = "empty" | "draft" | "published";

export interface WeeklyGridData {
  week_start: string;          // YYYY-MM-DD (T2)
  week_end: string;            // YYYY-MM-DD (CN)
  week_number: number;         // Tuần ISO
  status: ScheduleWeekStatus;
  dirty_after_publish: boolean; // D-6: badge kép [8b] + bật [Xuất bản lại] + khoá thao tác
  has_restorable_snapshot: boolean; // D-7: có bản snapshot để [Hủy thay đổi] khôi phục không
  is_current_week: boolean;
  is_past_week: boolean;       // tuần quá khứ → read-only (biến thể C)
  branch_name: string | null;
  branch_code: string | null;
  rules: ScheduleRulesInfo;
  groups: GridWorkAreaGroup[];
  warnings_count: number;
  warnings_summary: string | null; // banner [4] verbatim server
  as_of: string;
}

export interface WeeklyGridParams {
  week_start: string;          // YYYY-MM-DD — ngày bất kỳ trong tuần
  work_area_id?: string;
}

// ── [6] Panel "Nhật ký thay đổi lịch" (2026-09-14, wireframe [6]/[6c]/[6e] —
// WIREFRAME-V0-MASTER-REGISTRY.md §SC-009 dòng ~3067-3170) — mirror
// backend AuditActorInfo/AuditAffectedStaff/AuditAffectedInfo/AuditHistoryItem/
// AuditHistoryResponse (manager_schedule.py dòng ~755-843). ──

export interface ScheduleAuditActorInfo {
  id: string;
  full_name: string;
  role: string | null;
}

export interface ScheduleAuditAffectedStaff {
  id: string;
  full_name: string;
}

/**
 * 11 giá trị route (7 gán + 4 gỡ) theo wireframe [6c] — `range` ("Chọn khoảng
 * ngày") nằm trong domain nhưng KHÔNG có write-site nào tạo ra hiện tại (FE
 * chưa dựng gesture chọn-khoảng-ngày-qua-lịch cho panel này — chỉ có kéo-chọn
 * vùng `[2b]` → route=rect). Giữ trong type để không phải đổi khi FE dựng thêm.
 */
export type ScheduleAuditRoute =
  | "cell"
  | "rect"
  | "range"
  | "quick_entry"
  | "import"
  | "copy"
  | "proposal_approve"
  | "remove_single"
  | "remove_day"
  | "bulk_off"
  | "proposal_reject";

export type ScheduleAuditContentKind = "shift_code" | "custom_hours" | "custom_hours_ot";

export interface ScheduleAuditAffectedInfo {
  /** Nhóm THÔ: assign | remove | publish | save_draft | import */
  op: string;
  /** RỖNG cho route=copy (và mọi route khác backend chưa capture theo NV cụ
   * thể) — 1 dòng audit đại diện cả batch, KHÔNG suy diễn/bịa tên. */
  staff: ScheduleAuditAffectedStaff[];
  date: string | null;
  shift_code: string | null;
  schedule_id: string | null;
  route: ScheduleAuditRoute | null;
  content_kind: ScheduleAuditContentKind | null;
  /** CHỈ có ở route=copy */
  source_date: string | null;
  /** true ở route=proposal_approve|proposal_reject */
  is_self_proposed: boolean | null;
}

export interface ScheduleAuditHistoryItem {
  id: string;
  action: string;
  action_label_vi: string;
  actor: ScheduleAuditActorInfo;
  created_at: string;
  week_start: string;
  affected: ScheduleAuditAffectedInfo;
  success: boolean;
}

export interface ScheduleAuditHistoryData {
  items: ScheduleAuditHistoryItem[];
  next_cursor: string | null;
}

export interface ScheduleAuditHistoryParams {
  week_start: string; // YYYY-MM-DD — bất kỳ ngày nào trong tuần panel đang xem
  entity_id?: string;
  cursor?: string;
  limit?: number;
}

// Employee tự đăng ký ca → Manager duyệt (2026-08-09) — song song với xếp ca
// trực tiếp phía trên, KHÔNG thay thế.
export interface ScheduleApprovalResult {
  updated_count: number;
  schedule_ids: string[];
  dirty_after_publish: boolean;
  audited: boolean;
}

export interface WeekApprovalPayload {
  tenant_user_id: string;
  week_start: string;
  approved: boolean;
  reason?: string; // bắt buộc khi approved=false
}

export interface ScheduleCellPayload {
  user_id: string;
  date: string;                // YYYY-MM-DD — ngày của ô
  shift_template_id?: string;  // gán ca từ danh mục (combobox [13]); kèm schedule_id = SỬA mã ca (A1)
  schedule_id?: string;        // xoá 1 ca cụ thể trong ô; kèm shift_template_id = SỬA mã ca (A1)
  remove?: boolean;            // true → bỏ hết ca ô (Nghỉ (bỏ hết ca))
  /** 2026-08-12: điều động — ca ở cơ sở KHÁC cơ sở/bộ phận mặc định của NV */
  organization_id?: string;
  /** 2026-08-12: ghi chú của Manager (VD lý do điều động) */
  manager_note?: string;
}

export interface ScheduleCellResult {
  action: string;              // assigned | updated | removed | noop
  schedule_id: string | null;
  removed_count: number;
  // [R42] (2026-09-15, vá FE — backend đã có field từ 2026-09-07 GAP #1):
  // "Nghỉ (bỏ hết ca)" (remove=true, không kèm schedule_id) chỉ xoá khối
  // CHƯA bắt đầu — khối đã bắt đầu bị GIỮ LẠI, đếm ở đây (WIREFRAME-V0-MASTER-
  // REGISTRY.md §SC-009 [R42] "Hai tầng khoá": dòng "Còn {N} khối đã bắt đầu,
  // không xoá được."). Luôn 0 cho đường xoá-1-ca-cụ-thể (schedule_id) — đường
  // đó 422 thẳng khi khối đã bắt đầu, không có khái niệm "giữ lại một phần".
  // Khớp `CellUpdateResponse.locked_count` (manager_schedule.py quanh dòng 365).
  locked_count: number;
  warnings: string[];          // mode=warn → cho qua + warnings (mode=block → 422)
  dirty_after_publish: boolean;
  audited: boolean;
}

export interface ScheduleViolationItem {
  user_id: string | null;
  full_name: string | null;
  type: string;                // over_cap | min_rest
  message: string;             // lý do VI
}

export interface PublishWeekResult {
  week_start: string;
  week_end: string;
  published_count: number;
  re_publish: boolean;
  warnings: ScheduleViolationItem[];
  notified_employees: boolean;
  audited: boolean;
}

export interface DiscardWeekChangesResult {
  week_start: string;
  week_end: string;
  restored_count: number;
  discarded_new_count: number;
  dirty_after_publish: boolean;
  audited: boolean;
}

export interface SaveDraftResult {
  week_start: string;
  week_end: string;
  draft_count: number;
  published_count: number;
  audited: boolean;
}

export interface ShiftCodeItem {
  id: string;
  code: string | null;
  name: string;
  start_time: string;          // "HH:MM"
  end_time: string;
  shift_type: string | null;
  hours: number;
  color_code: string | null;
  /** A4 (2026-09-03): mã ca ngày nghỉ — combobox hiện "Ngày nghỉ", gán được mọi ngày (ECO-DEC-2026-144) */
  is_day_off?: boolean;
}

export interface ShiftCodesData {
  items: ShiftCodeItem[];
  total: number;
}

// =============================================================================
// VĐ1 (2026-08-16): "Phân ca" hàng loạt + điều động liên chi nhánh
// =============================================================================

export interface BranchOption {
  id: string;
  name: string;
  code: string;
}

export interface BranchListData {
  items: BranchOption[];
  total: number;
}

export interface BulkAssignPayload {
  employee_ids: string[];
  shift_template_id: string;
  date_start: string; // YYYY-MM-DD
  date_end: string;   // YYYY-MM-DD (inclusive)
  target_organization_id?: string | null; // null/undefined = giữ chi nhánh hiện tại
}

// [OT] — "Phân theo giờ" khối giờ tuỳ chỉnh (KHÔNG gắn mã ca), tuỳ chọn
// "Tính là OT". Cùng hình dạng rectangle với BulkAssignPayload, khác payload
// (giờ tuỳ ý thay vì shift_template_id) nên endpoint riêng.
export interface BulkAssignHoursPayload {
  employee_ids: string[];
  date_start: string; // YYYY-MM-DD
  date_end: string;   // YYYY-MM-DD (inclusive)
  start_time: string; // HH:MM
  end_time: string;   // HH:MM — end < start = ca qua đêm (hợp lệ)
  is_overtime: boolean;
  target_organization_id?: string | null;
}

export interface BulkAssignSkipItem {
  employee_id: string;
  date: string;
  reason: string;
}

export interface BulkAssignResult {
  assigned_count: number;
  skipped: BulkAssignSkipItem[];
  // violation_mode="warn" → ca VẪN được tạo (nằm trong assigned_count,
  // KHÔNG trong `skipped`) nhưng có vi phạm chồng-giờ/nghỉ-tối-thiểu — cùng
  // shape BulkAssignSkipItem (employee_id/date/reason).
  warnings: BulkAssignSkipItem[];
  is_cross_branch: boolean;
  target_branch_name: string | null;
  notified_count: number;
  audited: boolean;
}

// [2f] BR-SCHD-013 (2026-09-07, 🆕 CHƯA CÓ BR chính thức — wireframe là nguồn
// đầu tiên): "Nghỉ hàng loạt" — cùng hình dạng rectangle (employee_ids ×
// [date_start, date_end]) với BulkAssignPayload, chỉ khác thao tác ghi.
export interface BulkOffPayload {
  employee_ids: string[];
  date_start: string; // YYYY-MM-DD
  date_end: string;   // YYYY-MM-DD (inclusive)
}

export interface BulkOffSkipItem {
  employee_id: string;
  date: string;
  reason: string;
}

export interface BulkOffResult {
  removed_count: number;
  removed_schedule_ids: string[];
  // [R42]: khối đã bắt đầu → không xoá được, liệt ở đây thay vì lỗi cả batch.
  skipped: BulkOffSkipItem[];
  audited: boolean;
}

// BR-SCHD-011 (2026-09-07, 🆕 CHƯA CÓ BR chính thức — wireframe là nguồn đầu
// tiên): [5] "Nhập nhanh bằng văn bản" — FE parse text thành assignments đã
// khớp employee_code/tên, backend chỉ nhận danh sách đã parse để ghi.
export interface QuickEntryAssignment {
  user_id: string;
  date: string; // YYYY-MM-DD
  shift_template_id: string;
}

export interface QuickEntryPayload {
  date_start: string; // YYYY-MM-DD — khoảng áp dụng
  date_end: string;   // YYYY-MM-DD (inclusive)
  assignments: QuickEntryAssignment[];
}

export interface QuickEntrySkipItem {
  user_id: string;
  date: string;
  reason: string;
}

export interface QuickEntryResult {
  assigned_count: number;
  skipped: QuickEntrySkipItem[];
  audited: boolean;
}

export interface ScheduleImportRow {
  row_number: number;
  staff_input: string;
  date_input: string;
  shift_code_input: string;
  department_input: string | null;
  valid: boolean;
  reason: string | null;
}

export interface ImportPreviewCell {
  shift_code: string;
  valid: boolean;
  reason: string | null;
}

export interface ImportPreviewDay {
  date: string;
  day_label: string;
  cells: ImportPreviewCell[];
}

export interface ImportPreviewEmployee {
  user_id: string | null;
  full_name: string;
  work_area_name: string | null;
  out_of_scope: boolean;
  days: ImportPreviewDay[];
}

export interface ImportAssignment {
  user_id: string;
  date: string;
  shift_template_id: string;
}

export interface ImportPreviewData {
  week_start: string;
  week_end: string;
  branch_name: string | null;
  branch_code: string | null;
  rows: ScheduleImportRow[];
  preview_grid: ImportPreviewEmployee[];
  valid_count: number;
  error_count: number;
  summary: string;             // "✔ N gán hợp lệ · ✖ M lỗi (ô tô cảnh báo). …"
  valid_assignments: ImportAssignment[];
}

export interface ImportCommitResult {
  imported: number;
  skipped: number;
  /** Đếm theo lý do: out_of_week|scope_invalid|no_org_anchor|duplicate|rest_violation */
  skipped_reasons: Record<string, number>;
  audited: boolean;
}

export const IMPORT_SKIP_REASON_LABELS_VI: Record<string, string> = {
  out_of_week: "ngày ngoài tuần",
  scope_invalid: "nhân viên/mã ca không hợp lệ",
  no_org_anchor: "thiếu đơn vị tổ chức",
  duplicate: "trùng ca đã có",
  rest_violation: "vi phạm nghỉ giữa 2 ca",
  // [R42] (2026-09-07): ngày/giờ bắt đầu ca đã qua — cùng cổng Nhập nhanh
  past_r42: "đã qua giờ bắt đầu ca",
  // #799: copy skips shifts that are not official (backend `_is_official_schedule`)
  rejected_proposal: "đề xuất đã bị từ chối",
  not_official: "ca chưa chính thức (đề xuất chờ duyệt / đang chờ huỷ)",
};

/** POST /manager/schedule/weekly-grid/copy-from-week response (S-MGR-COPY 2026-08-09) */
export interface CopyWeekResult {
  copied: number;
  skipped: number;
  skipped_reasons: Record<string, number>;
  audited: boolean;
}

/**
 * POST /manager/schedule/weekly-grid/copy-range-preview|-commit (2026-09-14,
 * generalize copy-from-week — WIREFRAME-V0-MASTER-REGISTRY.md §SC-009
 * `[17]/[17b]`). Mirror backend `CopyRangeRequest` (manager_schedule.py):
 * khoảng NGUỒN/ĐÍCH tuỳ ý (không ép 7 ngày) — nguồn dài N, đích dài M, LẶP
 * THEO CHU KỲ NGUỒN (ngày thứ i đích lấy ca từ ngày thứ (i mod N) nguồn, cắt
 * cụt ở cuối).
 */
export interface CopyRangeRequestPayload {
  source_from: string;
  source_to: string;
  target_from: string;
  target_to: string;
}

/** Mirror backend `CopyRangePlanItem` — 1 ca SẼ được chép, đủ để vẽ bảng "Xem trước bản sao chép" `[17b]`. */
export interface CopyRangePlanItem {
  target_date: string;
  source_date: string;
  user_id: string;
  full_name: string;
  shift_label: string;
  start_time: string;
  end_time: string;
}

/** Mirror backend `CopyRangeSkipItem` — cùng bộ lý do dùng bởi `IMPORT_SKIP_REASON_LABELS_VI` ở trên. */
export interface CopyRangeSkipItem {
  date: string;
  user_id: string | null;
  full_name: string | null;
  reason: string;
}

/** Mirror backend `CopyRangePreviewResponse` — response `copy-range-preview` (dry_run=True, KHÔNG ghi DB). */
export interface CopyRangePreviewResult {
  items: CopyRangePlanItem[];
  skipped: CopyRangeSkipItem[];
  source_days: number;
  target_days: number;
}

/** File tải về từ endpoint export (blob + tên file từ Content-Disposition) */
export interface ExportFileResult {
  blob: Blob;
  filename: string;
}

// =============================================================================
// BR-MGR-001 REQ-B — "Giao việc" tab types (mirror backend/app/schemas/task.py).
// Endpoints live on /api/v1/tasks/ (backend/app/api/v1/tasks.py), NOT /api/v2 —
// use the `api` axios instance (baseURL already /api/v1), not `apiV2`.
// Verified live 2026-08-17 against a real devdb (BR-MGR-001 §5.5 table):
// GET /tasks/ returns raw ORM rows with NO response_model (so no
// assigned_to_name/created_by_name — resolve assignee display name from the
// roster this screen already loads, per wireframe §3.1). Backend enum wire
// values are UPPERCASE (CLEANING/HIGH/PENDING…), ids are UUID strings — do
// NOT reuse the stale frontend/src/types/task.ts / services/api/task.ts
// (lowercase enum values, numeric ids — mismatched against the real backend,
// confirmed by direct code read, never fixed).
// =============================================================================

export type ManagerTaskCategory =
  | "CLEANING"
  | "PREPARATION"
  | "SERVICE"
  | "INVENTORY"
  | "MAINTENANCE"
  | "TRAINING"
  | "ADMINISTRATIVE"
  | "OTHER";

export type ManagerTaskPriority = "LOW" | "MEDIUM" | "HIGH" | "URGENT";

export type ManagerTaskStatus =
  | "PENDING"
  | "ASSIGNED"
  | "IN_PROGRESS"
  | "IN_REVIEW"
  | "COMPLETED"
  | "CANCELLED";

/** Mirrors TaskResponse (schemas/task.py) as actually returned by GET /tasks/
 * (list — no response_model, so created_by_name/assigned_to_name are NEVER
 * present; TaskDetailResponse-only fields like description/comments also
 * absent from the list route, confirmed via direct devdb probe 2026-08-17). */
export interface ManagerTaskItem {
  id: string;
  title: string;
  description?: string | null;
  category: ManagerTaskCategory;
  priority: ManagerTaskPriority;
  status: ManagerTaskStatus;
  created_by_id: string;
  assigned_to_id: string | null;
  branch_id: string | null;
  work_area_id: string | null;
  due_date: string | null;
  estimated_hours: number | null;
  created_at: string;
  updated_at: string;
  assigned_at: string | null;
  completion_percentage: number;
  is_overdue?: boolean;
}

export interface ManagerTaskCreateRequest {
  title: string;
  description?: string;
  category?: ManagerTaskCategory;
  priority?: ManagerTaskPriority;
  due_date?: string; // ISO datetime
  /** BR-MGR-001 §1.3.1 — MUST always be sent (Manager's own branch_id) or the
   * created task becomes invisible to that Manager's own branch-scoped list
   * (apply_branch_scope_filter does `Task.branch_id == user_branch_id`; SQL
   * NULL never matches). Confirmed by direct devdb repro 2026-08-17. */
  branch_id: string;
  assigned_to_id?: string;
}

export interface ManagerTaskAssignRequest {
  assigned_to_id: string;
  notes?: string;
}

export interface ManagerTaskBulkAssignRequest {
  task_ids: string[];
  assigned_to_id: string;
  notes?: string;
}

export interface ManagerTaskBulkAssignResponse {
  message: string;
  task_ids: string[];
}

// =============================================================================
// Query Parameter Types
// =============================================================================

export interface PendingApprovalsParams {
  page?: number;
  page_size?: number;
  approval_type?: "shift_swap" | "leave_request";
}

// =============================================================================
// Lịch sử nghỉ phép — tiền đề tổng quan "Phiếu Lương" (2026-08-30)
// =============================================================================

export interface LeaveHistoryParams {
  start_date?: string;   // YYYY-MM-DD
  end_date?: string;     // YYYY-MM-DD
  employee_id?: string;
  status?: string;       // approved | rejected | pending | manager_review | cancelled | submitted
  page?: number;
  page_size?: number;
}

export interface LeaveHistoryItem {
  id: string;
  employee_id: string;
  employee_name: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  working_days: number | null;   // số ngày công — dùng tính lương
  status: string;
  reason: string | null;
  rejection_reason: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  created_at: string | null;
}

export interface LeaveHistoryData {
  items: LeaveHistoryItem[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface AlertsParams {
  limit?: number;
}

// =============================================================================
// API Client — S75-002: real endpoints, no mocks
// =============================================================================

export const managerApi = {
  /**
   * GET /api/v2/analytics/manager/kpis
   * Real-time KPIs: present, late, absent, pending_approvals, on_time_rate
   */
  getKPIs: async (): Promise<ManagerKPIData> => {
    const response = await apiV2.get<ManagerKPIData>("/analytics/manager/kpis");
    return response.data;
  },

  /**
   * GET /api/v2/analytics/manager/alerts
   * Manager alerts: late arrivals, absent staff, pending approvals
   */
  getAlerts: async (params?: AlertsParams): Promise<ManagerAlertsData> => {
    const response = await apiV2.get<ManagerAlertsData>("/analytics/manager/alerts", {
      params,
    });
    return response.data;
  },

  /**
   * GET /api/v2/analytics/manager/active-staff
   * Currently clocked-in staff for manager's branch
   */
  getActiveStaff: async (): Promise<ActiveStaffData> => {
    const response = await apiV2.get<ActiveStaffData>("/analytics/manager/active-staff");
    return response.data;
  },

  /**
   * GET /api/v2/manager/pending-approvals
   * Aggregated pending approvals: shift_swaps + leave_requests
   */
  getPendingApprovals: async (params?: PendingApprovalsParams): Promise<PendingApprovalsData> => {
    const response = await apiV2.get<PendingApprovalsData>("/manager/pending-approvals", {
      params,
    });
    return response.data;
  },

  /**
   * GET /api/v2/manager/leave-history
   * Lịch sử nghỉ phép MỌI trạng thái, lọc theo khoảng ngày (Date & Time picker)
   * — tiền đề tổng quan "Phiếu Lương" (2026-08-30).
   */
  getLeaveHistory: async (params: LeaveHistoryParams): Promise<LeaveHistoryData> => {
    const response = await apiV2.get<LeaveHistoryData>("/manager/leave-history", { params });
    return response.data;
  },

  /**
   * POST /api/v2/manager/bulk-approve
   * Bulk approve/reject with audit trail
   * DoD: Vietnamese labels, reason required for reject
   */
  bulkApprove: async (payload: BulkApproveRequest): Promise<BulkApproveResponse> => {
    const response = await apiV2.post<BulkApproveResponse>("/manager/bulk-approve", payload);
    return response.data;
  },

  /**
   * GET /api/v2/analytics/manager/coverage-today
   * SC-010-HUB-BM widget [3]: độ phủ theo khu vực hôm nay (real DB, no mocks)
   */
  getCoverageToday: async (): Promise<CoverageTodayData> => {
    const response = await apiV2.get<CoverageTodayData>("/analytics/manager/coverage-today");
    return response.data;
  },

  /**
   * GET /api/v2/analytics/manager/week-summary
   * SC-010-HUB-BM widget [2]: lịch tuần hiện tại (status + cảnh báo)
   */
  getWeekSummary: async (): Promise<WeekSummaryData> => {
    const response = await apiV2.get<WeekSummaryData>("/analytics/manager/week-summary");
    return response.data;
  },

  /**
   * GET /api/v2/analytics/manager/hours-summary?period=week|month&anchor_date=
   * ISS-MGR-11 / DD-0093 widget MỚI: tổng giờ công ĐÃ CHẤM (không phải giờ theo
   * lịch) mỗi nhân viên trong tuần/tháng, scope theo chi nhánh.
   */
  getHoursSummary: async (params: HoursSummaryParams): Promise<HoursSummaryData> => {
    const response = await apiV2.get<HoursSummaryData>("/analytics/manager/hours-summary", {
      params,
    });
    return response.data;
  },

  /**
   * POST /api/v2/analytics/manager/remind-missing
   * SC-010-HUB-BM dialog [N]: nhắc NV chưa chấm công (v0: audit log + count thật)
   */
  remindMissing: async (payload: RemindMissingRequest): Promise<RemindMissingResponse> => {
    const response = await apiV2.post<RemindMissingResponse>("/analytics/manager/remind-missing", payload);
    return response.data;
  },

  // =========================================================================
  // Release v0 WP-3 / SC-011-ATTEND-MGR — Chấm công đội (mirror manager_attendance.py)
  // =========================================================================

  /**
   * GET /api/v2/manager/attendance?date&work_area_id&status
   * Bảng [6]: ca-lịch ↔ giờ-thực gom theo khu vực; ngày tương lai → kế hoạch ca
   */
  getTeamAttendance: async (params: TeamAttendanceParams): Promise<TeamAttendanceData> => {
    const response = await apiV2.get<TeamAttendanceData>("/manager/attendance", { params });
    return response.data;
  },

  /**
   * GET /api/v2/manager/branch-workforce-config — SC-011 modal [10]⚙.
   * `editable` cho biết Tenant Admin đã cấp quyền nhóm nào (SC-004 khối [9]);
   * nhóm chưa cấp phải hiện CHỈ-ĐỌC kèm lý do, không ẩn đi.
   */
  getBranchWorkforceConfig: async (): Promise<BranchWorkforceConfig> => {
    const response = await apiV2.get<BranchWorkforceConfig>("/manager/branch-workforce-config");
    return response.data;
  },

  /** PUT /api/v2/manager/branch-workforce-config — gửi key nào đặt key đó. */
  updateBranchWorkforceConfig: async (
    payload: Partial<BranchWorkforceConfigValues>,
  ): Promise<BranchWorkforceConfig> => {
    const response = await apiV2.put<BranchWorkforceConfig>("/manager/branch-workforce-config", payload);
    return response.data;
  },

  /**
   * GET /api/v2/manager/schedule/transfer-branches
   * 2026-08-12: danh mục cơ sở cho combobox "Điều động cơ sở khác" khi phân ca.
   */
  getTransferBranches: async (): Promise<{ id: string; name: string; code: string | null }[]> => {
    const response = await apiV2.get<{ items: { id: string; name: string; code: string | null }[] }>(
      "/manager/schedule/transfer-branches",
    );
    return response.data.items;
  },

  /**
   * GET /api/v2/manager/attendance/{id}/photo
   * 2026-08-12 (user báo lỗi): trước đây khung ảnh chấm công trên UI chỉ là
   * placeholder rỗng — ảnh đã lưu (mã hoá) sẵn trong DB nhưng chưa có endpoint
   * đọc cho Manager. Trả base64 THÔ (không kèm tiền tố data URI).
   * DD-0104 §3.3: backend giữ bản #637 (1 request trả CẢ 2 ảnh), không hỗ trợ
   * ?photo_type= — FE gọi 1 lần rồi hiển thị cả khung vào/ra. (Port lại từ
   * local/integration-2026-08-18 lúc merge PR #660 — endpoint vắng mặt trong
   * bản viết lại SC-011-ATTEND-MGR, xem manager_attendance.py get_attendance_photo.)
   */
  getAttendancePhoto: async (
    attendanceId: string,
  ): Promise<{ clock_in_photo: string | null; clock_out_photo: string | null }> => {
    const response = await apiV2.get<{ clock_in_photo: string | null; clock_out_photo: string | null }>(
      `/manager/attendance/${attendanceId}/photo`,
    );
    return response.data;
  },

  /**
   * POST /api/v2/manager/attendance/{id}/resolve — modal [18] "Ghi nhận công"/"Hoãn ghi nhận".
   * Giá trị gửi được LƯU NGUYÊN (không tự làm tròn phía server — chỉ auto-match mới làm tròn).
   */
  resolveAttendance: async (
    attendanceId: string,
    payload: ResolveRequest,
  ): Promise<ResolveResult> => {
    const response = await apiV2.post<ResolveResult>(
      `/manager/attendance/${attendanceId}/resolve`,
      payload,
    );
    return response.data;
  },

  /**
   * POST /api/v2/manager/attendance/schedule/{id}/resolve — cùng modal [18],
   * cho dòng "Thiếu chấm công": ca đã phân nhưng chưa có bản ghi chấm công nào,
   * nên chưa có `attendance_id` để gọi endpoint ở trên. Server tạo bản ghi theo
   * ca rồi lưu cùng một quyết định.
   */
  resolveAttendanceBySchedule: async (
    scheduleId: string,
    payload: ResolveRequest,
  ): Promise<ResolveResult> => {
    const response = await apiV2.post<ResolveResult>(
      `/manager/attendance/schedule/${scheduleId}/resolve`,
      payload,
    );
    return response.data;
  },

  /**
   * POST /api/v2/manager/attendance/bulk-resolve — checkbox cột "Thao tác" +
   * thanh bulk "N đã chọn". Mỗi dòng xử lý ĐỘC LẬP ở server — 1 dòng lỗi
   * không chặn các dòng còn lại, kết quả trả về theo từng `ref` trong
   * `results` để FE báo chính xác dòng nào thành công/lỗi.
   */
  bulkResolveAttendance: async (payload: BulkResolveRequest): Promise<BulkResolveResult> => {
    const response = await apiV2.post<BulkResolveResult>("/manager/attendance/bulk-resolve", payload);
    return response.data;
  },

  /** POST /api/v2/manager/attendance/{id}/approve — dialog [11] Duyệt.
   * #1143: Duyệt = trả đủ ca → lý do bắt buộc, chỉ trong 24 h (ECO-DEC-2026-144 W2). */
  approveAttendance: async (attendanceId: string, reason: string): Promise<AttendanceActionResult> => {
    const response = await apiV2.post<AttendanceActionResult>(
      `/manager/attendance/${attendanceId}/approve`,
      { reason },
    );
    return response.data;
  },

  /** POST /api/v2/manager/attendance/{id}/unapprove — menu ⁝ Bỏ duyệt */
  unapproveAttendance: async (attendanceId: string): Promise<AttendanceActionResult> => {
    const response = await apiV2.post<AttendanceActionResult>(
      `/manager/attendance/${attendanceId}/unapprove`,
    );
    return response.data;
  },

  /** POST /api/v2/manager/attendance/{id}/reject — bổ sung 2026-08-17 (yêu
   * cầu người dùng: nút "Từ chối" bản chấm công, tách biệt với "chờ duyệt") */
  rejectAttendance: async (attendanceId: string, reason: string): Promise<AttendanceActionResult> => {
    const response = await apiV2.post<AttendanceActionResult>(
      `/manager/attendance/${attendanceId}/reject`,
      { reason },
    );
    return response.data;
  },

  /** POST /api/v2/manager/attendance/{id}/flag — popover [10] Gắn cờ */
  flagAttendance: async (attendanceId: string, note: string): Promise<AttendanceActionResult> => {
    const response = await apiV2.post<AttendanceActionResult>(
      `/manager/attendance/${attendanceId}/flag`,
      { note },
    );
    return response.data;
  },

  /** POST /api/v2/manager/attendance/{id}/unflag — menu ⁝ Gỡ cờ */
  unflagAttendance: async (attendanceId: string): Promise<AttendanceActionResult> => {
    const response = await apiV2.post<AttendanceActionResult>(
      `/manager/attendance/${attendanceId}/unflag`,
    );
    return response.data;
  },

  /** POST /api/v2/manager/attendance/{id}/ot — popover [12] Đăng ký ngoài giờ */
  registerOt: async (attendanceId: string, payload: OTRequestPayload): Promise<AttendanceActionResult> => {
    const response = await apiV2.post<AttendanceActionResult>(
      `/manager/attendance/${attendanceId}/ot`,
      payload,
    );
    return response.data;
  },

  /** PUT /api/v2/manager/attendance/{id}/ot — menu ⁝ Sửa đăng ký (đã có OT) */
  updateOt: async (attendanceId: string, payload: OTRequestPayload): Promise<AttendanceActionResult> => {
    const response = await apiV2.put<AttendanceActionResult>(
      `/manager/attendance/${attendanceId}/ot`,
      payload,
    );
    return response.data;
  },

  /**
   * POST /api/v2/manager/attendance/schedule/{schedule_id}/ot
   * Đăng ký OT TRƯỚC cho ca tương lai / dòng Thiếu chấm (chưa có attendance)
   */
  registerOtBySchedule: async (scheduleId: string, payload: OTRequestPayload): Promise<AttendanceActionResult> => {
    const response = await apiV2.post<AttendanceActionResult>(
      `/manager/attendance/schedule/${scheduleId}/ot`,
      payload,
    );
    return response.data;
  },

  // =========================================================================
  // Release v0 WP-4 / SC-012-MGR-STAFF — Nhân viên (mirror manager_staff.py)
  // =========================================================================

  /**
   * GET /api/v2/manager/staff?date=
   * Bảng [5]: roster NV trong scope + trạng thái chấm công hôm nay
   */
  getManagerStaff: async (date?: string): Promise<StaffListData> => {
    const response = await apiV2.get<StaffListData>("/manager/staff", {
      params: date ? { date } : undefined,
    });
    return response.data;
  },

  /**
   * GET /api/v2/manager/staff/{user_id}/week-detail
   * Modal [D] CHỈ ĐỌC: ca tuần này (7 ngày, mã ca/giờ) + chấm công hôm nay per ca
   */
  getStaffWeekDetail: async (userId: string): Promise<StaffWeekDetailData> => {
    const response = await apiV2.get<StaffWeekDetailData>(
      `/manager/staff/${userId}/week-detail`,
    );
    return response.data;
  },

  // =========================================================================
  // Release v0 WP-6 / SC-009-SCHEDULE-GRID — Bảng phân ca tuần (mirror manager_schedule.py)
  // =========================================================================

  /**
   * GET /api/v2/manager/schedule/weekly-grid?week_start&work_area_id
   * Lưới [5]: NV × 7 ngày gom khu vực ▾ + trạng thái tuần + banner cảnh báo [4]
   */
  getWeeklyGrid: async (params: WeeklyGridParams): Promise<WeeklyGridData> => {
    const response = await apiV2.get<WeeklyGridData>("/manager/schedule/weekly-grid", {
      params,
    });
    return response.data;
  },

  /**
   * GET /api/v2/manager/schedule/audit-history?week_start&entity_id&cursor&limit
   * Panel [6] "Nhật ký thay đổi lịch" — phân trang cursor ("Xem thêm (cũ hơn)").
   */
  getScheduleAuditHistory: async (
    params: ScheduleAuditHistoryParams,
  ): Promise<ScheduleAuditHistoryData> => {
    const response = await apiV2.get<ScheduleAuditHistoryData>(
      "/manager/schedule/audit-history",
      { params },
    );
    return response.data;
  },

  /** GET /api/v2/manager/schedule/shift-codes — danh mục mã ca combobox [13] */
  getScheduleShiftCodes: async (): Promise<ShiftCodesData> => {
    const response = await apiV2.get<ShiftCodesData>("/manager/schedule/shift-codes");
    return response.data;
  },

  /** GET /api/v2/manager/schedule/branches — VĐ1: dropdown chi nhánh đích khi điều động */
  getManagerBranches: async (): Promise<BranchListData> => {
    const response = await apiV2.get<BranchListData>("/manager/schedule/branches");
    return response.data;
  },

  /** POST /api/v2/manager/schedule/bulk-assign — VĐ1: popup [+] Phân ca hàng loạt */
  bulkAssignSchedule: async (payload: BulkAssignPayload): Promise<BulkAssignResult> => {
    const response = await apiV2.post<BulkAssignResult>(
      "/manager/schedule/bulk-assign",
      payload,
    );
    return response.data;
  },

  /** POST /api/v2/manager/schedule/weekly-grid/bulk-assign-hours — [OT] "Phân
   * theo giờ": khối giờ tuỳ chỉnh + tuỳ chọn "Tính là OT" (SC-009 §[OT]). */
  bulkAssignHoursSchedule: async (payload: BulkAssignHoursPayload): Promise<BulkAssignResult> => {
    const response = await apiV2.post<BulkAssignResult>(
      "/manager/schedule/weekly-grid/bulk-assign-hours",
      payload,
    );
    return response.data;
  },

  /** POST /api/v2/manager/schedule/bulk-off — [2f] BR-SCHD-013 "Nghỉ hàng loạt" */
  bulkOffSchedule: async (payload: BulkOffPayload): Promise<BulkOffResult> => {
    const response = await apiV2.post<BulkOffResult>(
      "/manager/schedule/bulk-off",
      payload,
    );
    return response.data;
  },

  /** POST /api/v2/manager/schedule/weekly-grid/quick-entry — modal [5] "Nhập nhanh bằng văn bản" (BR-SCHD-011) */
  quickEntrySchedule: async (payload: QuickEntryPayload): Promise<QuickEntryResult> => {
    const response = await apiV2.post<QuickEntryResult>(
      "/manager/schedule/weekly-grid/quick-entry",
      payload,
    );
    return response.data;
  },

  /**
   * POST /api/v2/manager/schedule/weekly-grid/cells
   * Popover [13]: gán ca / xoá 1 ca / bỏ hết ca ô. Mode Chặn → 422 {message, violations}
   */
  updateScheduleCell: async (payload: ScheduleCellPayload): Promise<ScheduleCellResult> => {
    const response = await apiV2.post<ScheduleCellResult>(
      "/manager/schedule/weekly-grid/cells",
      payload,
    );
    return response.data;
  },

  // =========================================================================
  // Đổi ca — Variant B (DD-0106) — backend/app/api/v2/endpoints/schedule.py
  // =========================================================================

  /**
   * GET /api/v2/schedule/swap/pending-manager-review
   * Danh sách request đã được NV target đồng ý, chờ Quản Lý duyệt.
   */
  getPendingSwapReviews: async (): Promise<ShiftSwapListItem[]> => {
    const response = await apiV2.get<ShiftSwapListItem[]>("/schedule/swap/pending-manager-review");
    return response.data;
  },

  /**
   * POST /api/v2/schedule/swap/approve
   * Quản Lý duyệt/từ chối — approved=true mới thật sự chuyển ca sang target.
   */
  approveSwapRequest: async (payload: ShiftSwapApprovalPayload): Promise<ShiftSwapApprovalResult> => {
    const response = await apiV2.post<ShiftSwapApprovalResult>("/schedule/swap/approve", payload);
    return response.data;
  },

  /** POST /api/v2/manager/schedule/weekly-grid/publish — nút [12] (+ Xuất bản lại D-6) */
  publishScheduleWeek: async (weekStart: string): Promise<PublishWeekResult> => {
    const response = await apiV2.post<PublishWeekResult>(
      "/manager/schedule/weekly-grid/publish",
      { week_start: weekStart },
    );
    return response.data;
  },

  /**
   * POST /api/v2/manager/schedule/weekly-grid/discard-changes — nút [Hủy thay
   * đổi] popup [18]: khôi phục về ĐÚNG bản đã xuất bản gần nhất (sửa quay lại
   * giá trị cũ, xoá được khôi phục, thêm-sau-publish bị huỷ). 422 nếu tuần
   * chưa từng xuất bản.
   */
  discardScheduleWeekChanges: async (weekStart: string): Promise<DiscardWeekChangesResult> => {
    const response = await apiV2.post<DiscardWeekChangesResult>(
      "/manager/schedule/weekly-grid/discard-changes",
      { week_start: weekStart },
    );
    return response.data;
  },

  /** POST /api/v2/manager/schedule/weekly-grid/save-draft — nút [11] (audit checkpoint) */
  saveScheduleDraft: async (weekStart: string): Promise<SaveDraftResult> => {
    const response = await apiV2.post<SaveDraftResult>(
      "/manager/schedule/weekly-grid/save-draft",
      { week_start: weekStart },
    );
    return response.data;
  },

  /**
   * GET /api/v2/manager/schedule/weekly-grid/export?week_start&format=csv|xlsx
   * Menu [10m]: tải file thật (blob + filename từ Content-Disposition)
   */
  exportWeeklyGrid: async (weekStart: string, format: "csv" | "xlsx"): Promise<ExportFileResult> => {
    const response = await apiV2.get<Blob>("/manager/schedule/weekly-grid/export", {
      params: { week_start: weekStart, format },
      responseType: "blob",
    });
    const disposition = (response.headers?.["content-disposition"] as string | undefined) ?? "";
    const match = /filename="?([^";]+)"?/.exec(disposition);
    return {
      blob: response.data,
      filename: match?.[1] ?? `lich-tuan-${weekStart}.${format}`,
    };
  },

  /**
   * POST /api/v2/manager/schedule/weekly-grid/import/preview (multipart)
   * Modal [17]→[18]: parse .csv/.xlsx → lưới preview + per-dòng ✔/✖ (KHÔNG ghi DB)
   */
  importSchedulePreview: async (file: File, weekStart: string): Promise<ImportPreviewData> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("week_start", weekStart);
    const response = await apiV2.post<ImportPreviewData>(
      "/manager/schedule/weekly-grid/import/preview",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  },

  /** POST /api/v2/manager/schedule/weekly-grid/import/commit — [Nhập N gán hợp lệ] */
  importScheduleCommit: async (
    weekStart: string,
    assignments: ImportAssignment[],
  ): Promise<ImportCommitResult> => {
    const response = await apiV2.post<ImportCommitResult>(
      "/manager/schedule/weekly-grid/import/commit",
      { week_start: weekStart, assignments },
    );
    return response.data;
  },

  /**
   * POST /api/v2/manager/schedule/weekly-grid/copy-from-week (S-MGR-COPY 2026-08-09)
   * Sao chép toàn bộ ca từ tuần nguồn sang tuần đích — 1 trong 3 giải pháp
   * cho vấn đề nhập tay 100 NV, tránh phải gõ lại từng ô cho tuần lặp lại.
   */
  copyScheduleFromWeek: async (
    sourceWeekStart: string,
    targetWeekStart: string,
  ): Promise<CopyWeekResult> => {
    const response = await apiV2.post<CopyWeekResult>(
      "/manager/schedule/weekly-grid/copy-from-week",
      { source_week_start: sourceWeekStart, target_week_start: targetWeekStart },
    );
    return response.data;
  },

  /**
   * POST /api/v2/manager/schedule/weekly-grid/copy-range-preview (2026-09-14)
   * Modal "Sao chép lịch" [17b]: tính (KHÔNG ghi DB) danh sách ca sẽ chép từ
   * khoảng NGUỒN sang khoảng ĐÍCH tuỳ ý. Gọi lại mỗi khi đổi khoảng nguồn
   * trong modal để cập nhật bảng xem trước.
   */
  copyRangePreview: async (payload: CopyRangeRequestPayload): Promise<CopyRangePreviewResult> => {
    const response = await apiV2.post<CopyRangePreviewResult>(
      "/manager/schedule/weekly-grid/copy-range-preview",
      payload,
    );
    return response.data;
  },

  /**
   * POST /api/v2/manager/schedule/weekly-grid/copy-range-commit (2026-09-14)
   * TÍNH LẠI TỪ ĐẦU ở server (KHÔNG nhận plan preview đã render — vá L-03 của
   * prototype, tránh race-condition dữ liệu đổi giữa lúc xem trước và lúc ghi
   * thật) rồi ghi thật + audit log. Body giống hệt copy-range-preview; response
   * khuôn `CopyWeekResult` — GIỐNG HỆT copy-from-week (copied/skipped/skipped_reasons/audited).
   */
  copyRangeCommit: async (payload: CopyRangeRequestPayload): Promise<CopyWeekResult> => {
    const response = await apiV2.post<CopyWeekResult>(
      "/manager/schedule/weekly-grid/copy-range-commit",
      payload,
    );
    return response.data;
  },

  /** POST /api/v2/manager/schedule/approvals/{id}/approve — duyệt 1 ca Employee tự đề xuất */
  approveScheduleProposal: async (scheduleId: string): Promise<ScheduleApprovalResult> => {
    const response = await apiV2.post<ScheduleApprovalResult>(
      `/manager/schedule/approvals/${scheduleId}/approve`,
    );
    return response.data;
  },

  /** POST /api/v2/manager/schedule/approvals/{id}/reject — từ chối 1 ca, lý do bắt buộc */
  rejectScheduleProposal: async (scheduleId: string, reason: string): Promise<ScheduleApprovalResult> => {
    const response = await apiV2.post<ScheduleApprovalResult>(
      `/manager/schedule/approvals/${scheduleId}/reject`,
      { reason },
    );
    return response.data;
  },

  /** POST /api/v2/manager/schedule/approvals/week — duyệt/từ chối hàng loạt 1 tuần của 1 NV */
  reviewWeekProposals: async (payload: WeekApprovalPayload): Promise<ScheduleApprovalResult> => {
    const response = await apiV2.post<ScheduleApprovalResult>(
      "/manager/schedule/approvals/week",
      payload,
    );
    return response.data;
  },

  // ===========================================================================
  // BR-MGR-001 REQ-B — "Giao việc" tab (backend/app/api/v1/tasks.py, /api/v1)
  // ===========================================================================

  /**
   * GET /api/v1/tasks/ — branch-scoped list (apply_branch_scope_filter,
   * BR-PADM-008). No query params sent in v0 — backend already scopes by the
   * Manager's own branch server-side.
   */
  getTasks: async (): Promise<ManagerTaskItem[]> => {
    const response = await api.get<ManagerTaskItem[]>("/tasks/");
    return response.data;
  },

  /**
   * POST /api/v1/tasks/ — create (+ assign in one step if assigned_to_id set).
   * Caller MUST set branch_id explicitly — see ManagerTaskCreateRequest doc.
   */
  createTask: async (payload: ManagerTaskCreateRequest): Promise<ManagerTaskItem> => {
    const response = await api.post<ManagerTaskItem>("/tasks/", payload);
    return response.data;
  },

  /** POST /api/v1/tasks/{task_id}/assign — reassign ONE existing task. */
  assignTask: async (taskId: string, payload: ManagerTaskAssignRequest): Promise<ManagerTaskItem> => {
    const response = await api.post<ManagerTaskItem>(`/tasks/${taskId}/assign`, payload);
    return response.data;
  },

  /**
   * POST /api/v1/tasks/bulk/assign — reassign a SET of existing tasks to ONE
   * employee (not fan-out). Verified live against devdb 2026-08-17 with real
   * UUID payloads (TaskBulkAssign.assigned_to_id already UUID-typed, ISS-ARCH-20).
   */
  bulkAssignTasks: async (payload: ManagerTaskBulkAssignRequest): Promise<ManagerTaskBulkAssignResponse> => {
    const response = await api.post<ManagerTaskBulkAssignResponse>("/tasks/bulk/assign", payload);
    return response.data;
  },
};
